import { action, observable, makeObservable, runInAction, IObservableArray } from 'mobx';

import _ from 'lodash';

import { UuidableName } from 'utils/api/types';
import { TemplateDetailsResponse } from 'utils/api/modules/workflows/workflows.types';

import {
  KindOfQuestionTypes,
  MultipleAnswerProperties,
  MultipleAnswerQuestionType,
  QuestionType,
  TemplateContent,
} from './types';
import RootStore from '../Root';

interface FormOption {
  options: string[];
  scoring: string[] | null;
}
const arrayEquals = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((item, idx) => item === b[idx]);

const WORKFLOWS_TYPE_TO_JSON_TYPE: { [key: string]: KindOfQuestionTypes } = {
  location: 'object',
  signature: 'string',
  multi: 'array',
  date: 'string',
};

const resolveJsonType = (type: KindOfQuestionTypes): KindOfQuestionTypes => WORKFLOWS_TYPE_TO_JSON_TYPE[type] || type;

export default class DetailsTemplate {
  @observable uuid: string;

  @observable name: string;

  @observable content: TemplateContent;

  @observable isActive: boolean;

  @observable lastUpdate: string;

  @observable creator: UuidableName;

  @observable formOptionsObject: IObservableArray<FormOption>;

  @observable usedOptions: Array<FormOption>;

  @observable contentWasModified = false;

  @observable initialDataString: string;

  private savedSnapshotVersion = 0;

  private cachedSavedSnapshot: TemplateContent | undefined;

  private cachedSavedSnapshotVersion = -1;

  private addedSinceLastSave: Set<string> = new Set();

  store: RootStore;

  constructor(data: TemplateDetailsResponse, rootStore: RootStore) {
    this.store = rootStore;
    this.uuid = data.uuid;
    this.name = data.name;
    // @ts-ignore
    this.content = data.content;
    this.isActive = data.isActive;
    this.lastUpdate = data.lastUpdate;
    this.creator = data.creator;
    this.usedOptions = this.getUsedOptions();
    this.formOptionsObject = observable.array(this.usedOptions);
    this.initialDataString = JSON.stringify({ name: this.name, content: this.content });
    makeObservable(this);
  }

  sectionIdGenerator(order: string[]): string {
    return String(Math.max(...order.map(Number)) + 1);
  }

  isQuestionMandatory(questionId: string, sectionId: string): boolean {
    return this.content.properties[sectionId].mandatory.find((key) => key === questionId) !== undefined;
  }

  private getNewQuestionId(order: string[]): string {
    const numbers = order.map((element) => {
      const lastPart = element.split(':').pop();
      return Number(lastPart);
    });

    const result = String(Math.max(...numbers) + 1);
    return order.length > 0 ? result : String(order.length);
  }

  @action.bound
  getUsedOptions(): FormOption[] {
    const availableOptions: FormOption[] = [{ options: ['Yes', 'No'], scoring: null }];
    const seen = new Set<string>([JSON.stringify({ options: ['Yes', 'No'], scoring: null })]);

    Object.values(this.content.properties).forEach((section) => {
      Object.values(section.properties).forEach((q) => {
        if (q.workflowsType !== 'multi') return;
        const multi = q as MultipleAnswerQuestionType;
        const option: FormOption = {
          options: multi.items.enum,
          scoring: multi.scoring ?? null,
        };
        const key = JSON.stringify(option);
        if (!seen.has(key)) {
          seen.add(key);
          availableOptions.push(option);
        }
      });
    });

    return availableOptions;
  }

  @action.bound
  resetModified(): void {
    this.contentWasModified = false;
    this.initialDataString = JSON.stringify({ name: this.name, content: this.content });
    this.savedSnapshotVersion += 1;
    this.addedSinceLastSave.clear();
  }

  @action.bound
  editOption(options: string[], choice: string[], scoring: string[] | null): void {
    const newOption = {
      options,
      scoring,
    };
    const oldOption = {
      options: choice,
      scoring,
    };
    if (choice.length) {
      const index = this.formOptionsObject.findIndex((a) => _.isEqual(oldOption, a));
      if (index !== -1) {
        this.formOptionsObject[index] = newOption;
        this.contentWasModified = true;
        return;
      }
    }
    this.addNewOption(options, scoring);
  }

  @action.bound
  addNewOption(options: string[], scoring: string[] | null): void {
    if (scoring && scoring.length !== options.length) {
      return;
    }
    const newOption = {
      options,
      scoring,
    };
    if (!this.formOptionsObject.some((formOption) => _.isEqual(formOption, newOption))) {
      this.formOptionsObject.push(newOption);
      this.contentWasModified = true;
    }
  }

  @action.bound
  updateTemplateName(newName: string): void {
    this.name = newName;
    this.contentWasModified = true;
  }

  @action.bound
  changeSectionName(sectionId: string, value: string): void {
    this.content.properties[sectionId].description = value;
    this.contentWasModified = true;
  }

  @action.bound
  deleteOption(options: string[]): boolean {
    const index = this.getUsedOptions().findIndex((a) => options.every((v, i) => v === a.options[i]));

    if (index === -1) {
      const x = this.formOptionsObject.filter((item) => !arrayEquals(item.options, options));
      runInAction(() => {
        this.formOptionsObject.replace(x);
        this.contentWasModified = true;
      });
      return true;
    }
    this.store.notification.enqueueErrorSnackbar('Option is used in template.');
    return false;
  }

  haveDescription(): boolean {
    return Object.entries(this.content.properties).every(([key, section]) => {
      if (key !== '0' && section.description === '') return false;
      return Object.values(section.properties).every((q) => q.description !== '');
    });
  }

  @action.bound
  duplicateQuestion(questionId: string, sectionId: string): void {
    this.contentWasModified = true;
    const sectionProperties = this.content.properties[sectionId];

    const newQuestionId = this.getNewQuestionId(sectionProperties.order);
    const indexOf = sectionProperties.order.indexOf(questionId);
    const isMandatory = this.isQuestionMandatory(questionId, sectionId);
    const id = `${sectionId}::${newQuestionId}`;
    this.addedSinceLastSave.add(id);

    this.content = {
      ...this.content,
      properties: {
        ...this.content.properties,
        [sectionId]: {
          ...this.content.properties[sectionId],
          order: [...this.content.properties[sectionId].order, id],
          mandatory: [...this.content.properties[sectionId].mandatory],
          properties: {
            ...this.content.properties[sectionId].properties,
            [id]: this.content.properties[sectionId].properties[questionId],
          },
        },
      },
    };

    isMandatory && this.setQuestionAsMandatory(id, sectionId);

    this.reorder(this.content.properties[sectionId].order.indexOf(id), indexOf, sectionId);
  }

  @action.bound
  duplicateSections(sectionId: string): void {
    this.contentWasModified = true;
    const newSectionId = this.sectionIdGenerator(this.content.order);

    const { properties, mandatory } = this.content.properties[sectionId];

    const sectionProp = {};
    Object.keys(properties).map((questionKey) => {
      const newQuestionId = questionKey.split('::')[1];
      const fullId = `${newSectionId}::${newQuestionId}`;
      this.addedSinceLastSave.add(fullId);

      Object.assign(sectionProp, { [fullId]: properties[questionKey] });
    });

    this.content = {
      ...this.content,
      order: [...this.content.order, newSectionId],
      properties: {
        ...this.content.properties,
        [newSectionId]: {
          ...this.content.properties[sectionId],
          order: Object.keys(sectionProp),
          mandatory: mandatory.map((m) => `${newSectionId}::${m.split('::')[1]}`),
          properties: sectionProp,
        },
      },
    };

    this.reorderSections(this.content.order.indexOf(newSectionId), this.content.order.indexOf(sectionId) + 1);
  }

  @action.bound
  addQuestion(sectionId?: string): void {
    this.contentWasModified = true;
    const secId = sectionId || '0';
    const newQuestionId = this.getNewQuestionId(this.content.properties[secId].order);

    const id = `${secId}::${newQuestionId}`;
    this.addedSinceLastSave.add(id);

    this.content = {
      ...this.content,
      properties: {
        ...this.content.properties,
        [secId]: {
          ...this.content.properties[secId],
          order: [...this.content.properties[secId].order, id],
          properties: {
            ...this.content.properties[secId].properties,
            [id]: {
              description: '',
              type: 'boolean',
              workflowsType: 'boolean',
            },
          },
        },
      },
    };
  }

  @action.bound
  addSection(): void {
    this.contentWasModified = true;
    const newSectionId = this.sectionIdGenerator(this.content.order);

    this.content = {
      ...this.content,
      order: [...this.content.order, newSectionId],
      properties: {
        ...this.content.properties,
        [newSectionId]: {
          type: 'object',
          description: '',
          workflowsType: 'section',
          order: [],
          mandatory: [],
          properties: {},
        },
      },
    };
  }

  isQuestionSaved(questionId: string, sectionId: string): boolean {
    if (this.addedSinceLastSave.has(questionId)) {
      return false;
    }

    if (this.cachedSavedSnapshotVersion !== this.savedSnapshotVersion) {
      const parsed = JSON.parse(this.initialDataString) as { name: string; content: TemplateContent };
      this.cachedSavedSnapshot = parsed.content;
      this.cachedSavedSnapshotVersion = this.savedSnapshotVersion;
    }

    return this.cachedSavedSnapshot?.properties?.[sectionId]?.properties?.[questionId] !== undefined;
  }

  @action.bound
  replaceQuestionWithNewType(
    oldQuestionId: string,
    sectionId: string,
    newType: KindOfQuestionTypes,
    extraDefinition?: Partial<QuestionType>
  ): string {
    const section = this.content.properties[sectionId];
    const oldOrder = section.order;
    const oldIndex = oldOrder.indexOf(oldQuestionId);
    if (oldIndex === -1) {
      throw new Error(`replaceQuestionWithNewType: ${oldQuestionId} not found in section ${sectionId}`);
    }
    const oldQuestion = section.properties[oldQuestionId];

    const newQuestionId = `${sectionId}::${this.getNewQuestionId(oldOrder)}`;
    this.addedSinceLastSave.add(newQuestionId);

    const resolvedType = resolveJsonType(newType);

    const properties =
      newType === 'location'
        ? {
            lng: '',
            lat: '',
            address: '',
          }
        : undefined;

    const carriedImage = 'image' in oldQuestion ? { image: oldQuestion.image } : {};

    const newQuestion: QuestionType = {
      ...carriedImage,
      description: oldQuestion.description,
      type: resolvedType,
      workflowsType: newType,
      ...(properties ? { properties } : {}),
      ...extraDefinition,
    } as QuestionType;

    const newOrder = [...oldOrder];
    newOrder.splice(oldIndex, 1, newQuestionId);

    const newMandatory = section.mandatory.map((id) => (id === oldQuestionId ? newQuestionId : id));

    const { [oldQuestionId]: _omitted, ...restProperties } = section.properties;

    this.content = {
      ...this.content,
      properties: {
        ...this.content.properties,
        [sectionId]: {
          ...section,
          order: newOrder,
          mandatory: newMandatory,
          properties: {
            ...restProperties,
            [newQuestionId]: newQuestion,
          },
        },
      },
    };

    this.contentWasModified = true;
    return newQuestionId;
  }

  @action.bound
  changeQuestionType(
    questionId: string,
    type: KindOfQuestionTypes,
    extraProps?: MultipleAnswerProperties,
    extraDefinition?: object,
    sectionId?: string
  ): void {
    this.contentWasModified = true;
    const secId = sectionId || '0';
    const newType = resolveJsonType(type);

    const prop =
      type === 'location'
        ? {
            lng: '',
            lat: '',
            address: '',
          }
        : extraProps;

    this.content = {
      ...this.content,
      properties: {
        ...this.content.properties,
        [secId]: {
          ...this.content.properties[secId],
          properties: {
            ...this.content.properties[secId].properties,
            [questionId]: {
              ...this.content.properties[secId].properties[questionId],
              type: newType,
              properties: prop,
              workflowsType: type,
              ...extraDefinition,
            },
          },
        },
      },
    };
  }

  @action.bound
  removeQuestion(questionId: string, sectionId: string): void {
    this.contentWasModified = true;
    const sectionContent = this.content.properties[sectionId];
    const { [questionId]: _omitted, ...restProperties } = sectionContent.properties;

    this.content = {
      ...this.content,
      properties: {
        ...this.content.properties,
        [sectionId]: {
          ...sectionContent,
          mandatory: sectionContent.mandatory.filter((key) => key !== questionId),
          order: sectionContent.order.filter((key) => key !== questionId),
          properties: restProperties,
        },
      },
    };
  }

  @action.bound
  setQuestionAsMandatory(questionId: string, sectionId: string): void {
    this.contentWasModified = true;
    this.content = {
      ...this.content,
      properties: {
        ...this.content.properties,
        [sectionId]: {
          ...this.content.properties[sectionId],
          mandatory: [...this.content.properties[sectionId].mandatory, questionId],
        },
      },
    };
  }

  @action.bound
  setQuestionAsOptional(questionId: string, sectionId: string): void {
    this.contentWasModified = true;
    this.content = {
      ...this.content,
      properties: {
        ...this.content.properties,
        [sectionId]: {
          ...this.content.properties[sectionId],
          mandatory: this.content.properties[sectionId].mandatory.filter((key) => key !== questionId),
        },
      },
    };
  }

  @action.bound
  updateDescription(questionId: string, newDescription: string, sectionId: string): void {
    this.contentWasModified = true;
    this.content.properties = {
      ...this.content.properties,
      [sectionId]: {
        ...this.content.properties[sectionId],
        properties: {
          ...this.content.properties[sectionId].properties,
          [questionId]: {
            ...this.content.properties[sectionId].properties[questionId],
            description: newDescription,
          },
        },
      },
    };
  }

  @action.bound
  reorder(source: number, destination: number, sectionId: string): void {
    this.contentWasModified = true;
    const result = Array.from(this.content.properties[sectionId].order);
    const [removed] = result.splice(source, 1);
    result.splice(destination, 0, removed);

    this.content = {
      ...this.content,
      properties: {
        ...this.content.properties,
        [sectionId]: {
          ...this.content.properties[sectionId],
          order: result,
        },
      },
    };
  }

  @action.bound
  reorderBetweenSections(
    source: number,
    destination: number,
    sourceSectionId: string,
    destinationSectionId: string
  ): void {
    this.contentWasModified = true;
    const { order: sourceSectionOrder, mandatory: sourceSectionMandatory } = this.content.properties[sourceSectionId];
    const { order: destinationSectionOrder, mandatory: destinationSectionMandatory } =
      this.content.properties[destinationSectionId];
    const [removed] = sourceSectionOrder.splice(source, 1);

    const newQuestionId = this.getNewQuestionId(destinationSectionOrder);

    const newId = `${destinationSectionId}::${newQuestionId}`;
    this.addedSinceLastSave.add(newId);

    destinationSectionOrder.splice(destination, 0, newId);

    const index = sourceSectionMandatory.indexOf(removed);
    if (index !== -1) {
      // If it is, remove it from sourceSectionMandatory
      sourceSectionMandatory.splice(index, 1);
      // And add the newId to destinationSectionMandatory
      destinationSectionMandatory.push(newId);
    }

    this.content = {
      ...this.content,
      properties: {
        ...this.content.properties,
        [sourceSectionId]: {
          ...this.content.properties[sourceSectionId],
          order: sourceSectionOrder,
          mandatory: sourceSectionMandatory,
        },
        [destinationSectionId]: {
          ...this.content.properties[destinationSectionId],
          order: destinationSectionOrder,
          mandatory: destinationSectionMandatory,
          properties: {
            ...this.content.properties[destinationSectionId].properties,
            [newId]: {
              ...this.content.properties[sourceSectionId].properties[removed],
            },
          },
        },
      },
    };

    this.removeQuestion(removed, sourceSectionId);
  }

  @action.bound
  removeSection(sectionId: string, withQuestion: boolean): void {
    this.contentWasModified = true;
    const sectionsOrder = this.content.order;
    sectionsOrder.splice(this.content.order.indexOf(sectionId), 1);

    if (withQuestion) {
      this.content = {
        ...this.content,
        order: sectionsOrder,
        properties: {
          ...this.content.properties,
        },
      };

      delete this.content.properties[sectionId];
    } else {
      const sectionContent = this.content.properties[sectionId];
      const sectionZero = this.content.properties['0'];

      Object.keys(sectionContent.properties).map((prop) => {
        this.reorderBetweenSections(sectionContent.order.indexOf(prop), sectionZero.order.length, sectionId, '0');
      });

      delete this.content.properties[sectionId];
    }
  }

  @action
  reorderSections(source: number, destination: number): void {
    this.contentWasModified = true;
    const result = this.content.order;
    const [removed] = result.splice(source, 1);
    result.splice(destination, 0, removed);

    this.content = {
      ...this.content,
      order: result,
    };
  }

  getSavingCondition(details: DetailsTemplate | undefined): boolean {
    if (!details) return false;
    if (details.name.length === 0) return true;
    const hasSections = (details.content?.order || []).length > 1;

    if (hasSections) {
      return Object.entries(details.content.properties || {}).some(
        ([sectionKey, section]) => sectionKey !== '0' && section?.order.length === 0
      );
    }
    return details.content.properties['0'].order.length === 0;
  }
}
